import React, {
    ForwardRefRenderFunction,
    useImperativeHandle,
    forwardRef,
    useState,
    useEffect,
} from 'react';
import FeatureFlagged from '../authorization/feature-flagged';
import { FormattedMessage, WrappedComponentProps, useIntl } from 'react-intl';
import CheckboxInput from '../inputs/checkbox-input';
import Popover from '../common/popover';
import { IUserNotificationPreferences } from '../../models/user-notification-preferences';
import { FieldValidation, validate } from '../../validation/validation';
import * as Validators from '../../validation/validators';
import { IValidationHandles } from '../../models/validation-handles';
import TextInput from '../inputs/text-input';
import { optInOutRequest } from '../../store/profile/profile-actions';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import { RootState } from '../../store';

export class UserNotificationPrefsErrors {
    otherNotificationEmail? = '';
}

export interface IUserNotificationPreferencesProps {
    preferences: IUserNotificationPreferences;
    errors?: UserNotificationPrefsErrors;
    formClass?: string;
    onValueChange?: (item: IUserNotificationPreferences) => void;
    onChange?: (event: any) => void;
}

const UserNotificationPreferences: ForwardRefRenderFunction<
    IValidationHandles,
    IUserNotificationPreferencesProps
> = (props, ref) => {
    const { preferences, errors, formClass, onValueChange, onChange } = props;
    const dispatch = useAppDispatch();
    const username = useAppSelector(
        (state: RootState) => state.session!.data!.userName
    );
    const [clickedOptIn, setClickedOptIn] = useState(false);

    const intl = useIntl();

    const [errorState, setErrorState] = useState<UserNotificationPrefsErrors>(
        {}
    );
    const [preferencesState, setPreferencesState] =
        useState<IUserNotificationPreferences>({ ...preferences! });

    const formClassName =
        (formClass ? formClass : 'col-md-6') + ' form-horizontal';

    const fieldValidations = [
        new FieldValidation('otherNotificationEmail', ' ', [
            Validators.isEmail(
                intl.formatMessage({ id: 'Invalid_Email_Address' })
            ),
        ]),
    ];

    useEffect(() => {
        if (errors && errors !== errorState) {
            setErrorState(errors!);
        }
    }, [errors]);

    useEffect(() => {
        if (preferences) {
            setPreferencesState({ ...preferences });
        }
    }, [preferences]);

    const isValid = (): boolean => {
        const errors = validate(preferencesState, fieldValidations);
        setErrorState(errors);

        return Object.keys(errors).length === 0;
    };

    useImperativeHandle(ref, () => ({
        isValid: () => {
            return isValid();
        },
    }));

    const handleChange = (e: any) => {
        if (onChange) {
            onChange(e);
        }

        if (onValueChange) {
            const { name, checked, value } = e.target;
            const updated = {
                ...preferences,
                [name]:
                    name === 'emailFlag' || name === 'smsFlag'
                        ? checked
                        : value,
            };
            onValueChange(updated);
        }
    };
    return (
        <>
            <FeatureFlagged features={['User Notification']}>
                <>
                    <div className="col-md-12">
                        <div className="form-group gutter-top">
                            <h3 className="underline">
                                <FormattedMessage
                                    id="Notification_Preferences"
                                    defaultMessage="Order Notification Preferences"
                                />
                            </h3>
                            <div>
                                <strong>
                                    <FormattedMessage
                                        id="Send_Me_Order_Status_Notifications_By"
                                        defaultMessage="Send me Order Status Notifications By"
                                    />
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formClassName}>
                        <div className="form-group col-sm-12">
                            <label className="control-label col-sm-2">
                                <FormattedMessage id="Email" />:
                            </label>
                            <div className="col-sm-10">
                                <CheckboxInput
                                    name="emailFlag"
                                    label={intl.formatMessage({
                                        id: 'Email_Notifications_Sent_To_User',
                                        defaultMessage:
                                            'Email notifications sent to User Name listed in My Profile section.',
                                    })}
                                    checked={preferencesState.emailFlag!}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <FeatureFlagged features={['User Text Notification']}>
                            <div className="form-group col-sm-12 flex">
                                <label className="control-label col-sm-2">
                                    <FormattedMessage id="Text" />:
                                </label>
                                <div>
                                    <div>
                                        {preferencesState.smsFlag! ? (
                                            <FormattedMessage
                                                id="SMS_Opted_In_Label"
                                                defaultMessage="You are currently opted in to receive SMS notifications. To opt out, click the button below."
                                            />
                                        ) : clickedOptIn ? (
                                            <FormattedMessage
                                                id="SMS_Opted_In_Awaiting_Confirmation"
                                                defaultMessage="You have requested to opt in to receive SMS notifications. Please check your phone for a confirmation message."
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="SMS_Opted_Out_Label"
                                                defaultMessage="You are currently opted out of receiving SMS notifications. To opt in, click the button below."
                                            />
                                        )}
                                    </div>
                                    {!clickedOptIn && (
                                        <button
                                            type="button"
                                            className={
                                                'btn mt-4' +
                                                (preferencesState.smsFlag!
                                                    ? ' btn-danger'
                                                    : ' btn-primary')
                                            }
                                            onClick={() => {
                                                dispatch(
                                                    optInOutRequest(
                                                        username,
                                                        preferencesState.cellNumber!,
                                                        !preferencesState.smsFlag!
                                                    )
                                                );
                                                handleChange({
                                                    target: {
                                                        name: 'smsFlag',
                                                        checked: false,
                                                        value: false,
                                                    },
                                                } as any);
                                                setClickedOptIn(
                                                    !preferencesState.smsFlag!
                                                );
                                            }}
                                        >
                                            {preferencesState.smsFlag!
                                                ? 'Opt Out'
                                                : 'Opt In'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </FeatureFlagged>

                        <div className="form-group">
                            <div className="col-sm-12 gutter-top">
                                <label>
                                    {intl.formatMessage({
                                        id: 'Forward_my_order_status_email',
                                        defaultMessage: `Forward my order status notification to the
                                    Email Address:`,
                                    })}{' '}
                                    <Popover
                                        content={intl.formatMessage({
                                            id: 'Email_Instead_Notification_Instructions',
                                            defaultMessage:
                                                'By entering an email address in this field, you are opting out of status notifications and are electing to forward them to the manually entered email address.',
                                        })}
                                    />
                                </label>
                            </div>
                            <div className="col-sm-12">
                                <TextInput
                                    name="otherNotificationEmail"
                                    labelClassName=" "
                                    fieldClassName="col-sm-12"
                                    label=""
                                    value={
                                        preferencesState.otherNotificationEmail ||
                                        ''
                                    }
                                    error={errorState.otherNotificationEmail}
                                    showError={true}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </>
            </FeatureFlagged>
        </>
    );
};

// cannot use injectIntl with forwardRef. Will need to manually pass intl.
export default forwardRef(UserNotificationPreferences);
